import {
  ClockUserIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "@phosphor-icons/react";
import { DateField, Show } from "@refinedev/antd";
import {
  Authenticated,
  useGetLocale,
  useGo,
  useNotification,
  useTranslate,
} from "@refinedev/core";
import { Button, CardProps, Flex, Popconfirm, Space, Typography } from "antd";
import dayjs from "dayjs";
import { addDateTz } from "pages/media/utils";
import React, { ReactNode, useState } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { OrganizationSubscription } from "types";
import useDeleteSubscription from "./hooks/useDeleteSubscription";
import { Breadcrumbs } from "components/Breadcrumbs";
import { useGetActiveSubscriptions } from "#src/hooks";
import { isTrialSubscription } from "../media/projects/helpers/helper";
import { SeatsQuotaCard } from "./components/SeatsQuotaCard";
import PricingModal from "./components/PricingModal";
import "dayjs/locale/fr";
import "dayjs/locale/en";

const SubscriptionDetail = () => {
  const { subscriptions, isLoading } = useGetActiveSubscriptions();
  const subscriptionId = useParams().id;

  return (
    <Authenticated key={"subscription"}>
      <Show
        breadcrumb={<Breadcrumbs />}
        goBack
        isLoading={isLoading}
        headerButtons={<></>}
      >
        {!isLoading && (
          <SubscriptionStatus
            id={subscriptionId}
            subscription={subscriptions}
          />
        )}
      </Show>
    </Authenticated>
  );
};
interface SubscriptionStatusProps extends CardProps {
  subscription: OrganizationSubscription;
}
const SubscriptionStatus: React.FC<SubscriptionStatusProps> = ({
  id,
  subscription,
}) => {
  const go = useGo();
  const t = useTranslate();
  const { open: notify } = useNotification();
  const [isPricingModalOpen, setIsPricingModalOpen] = useState(false);
  const isExpired = dayjs().isAfter(addDateTz(subscription?.expiry));
  const isCanceled = subscription?.status === "canceled";
  // Custom (sales-managed) plans can't be self-cancelled.
  const isCustom = subscription?.license_type === "Custom";
  const { deleteSubscription } = useDeleteSubscription(id);
  const locale = useGetLocale();
  const currentLocale = locale();
  const dayjsLocale = currentLocale?.split("-")[0] ?? "en";

  const period = dayjs()
    .locale(dayjsLocale)
    .to(addDateTz(subscription?.expiry));

  const expiryFormatted = dayjs(addDateTz(subscription?.expiry))
    .locale(dayjsLocale)
    .format("LL");

  const getTranslationKey = () => {
    if (isCanceled) return "pages.subscription.detail.canceled";
    if (isExpired) return "pages.subscription.expired";
    return "pages.subscription.valid";
  };
  const translationKey = getTranslationKey();

  const tParams = isCanceled
    ? ([translationKey] as const)
    : ([translationKey, { period }] as const);
  const text = t(tParams[0], tParams[1]);
  const isSoloPlan =
    subscription?.license_name?.toLowerCase().includes("solo") ||
    subscription?.subscribed_products?.[0]?.product_name
      ?.toLowerCase()
      .includes("solo");
  const actions: ReactNode[] = [];
  if (isCanceled || isExpired || isTrialSubscription(subscription)) {
    actions.push(
      <Button
        onClick={() => setIsPricingModalOpen(true)}
        key="button"
        type="primary"
        shape="round"
      >
        {t("pages.subscription.buttons.subscribeNow")}
      </Button>
    );
  } else if (!isCustom) {
    actions.push(
      <Popconfirm
        onConfirm={async () => {
          try {
            await deleteSubscription();
            notify?.({
              type: "success",
              message: t("pages.subscription.cancel.success"),
            });
            go({ to: "/" });
          } catch {
            notify?.({
              type: "error",
              message: t("pages.subscription.cancel.error"),
            });
          }
        }}
        title={t("pages.subscription.cancel.description")}
      >
        <Button shape="round" danger>
          {t("pages.subscription.buttons.cancelSubscription")}
        </Button>
      </Popconfirm>
    );
  }
  const getStatusIcon = () => {
    if (isCanceled)
      return <XCircleIcon size={24} weight="bold" aria-hidden="true" />;
    if (isExpired)
      return <ClockUserIcon size={24} weight="bold" aria-hidden="true" />;
    return <CheckCircleIcon size={24} weight="bold" aria-hidden="true" />;
  };
  const statusIcon = getStatusIcon();
  return (
    <Flex vertical gap={20}>
      <Flex vertical>
        <StyledTitle level={4}>
          <Flex align="center" gap={5}>
            {statusIcon} {text}
          </Flex>
        </StyledTitle>
        {isCanceled && (
          <Typography.Text>
            {t("pages.subscription.detail.canceledLastDay", {
              date: expiryFormatted,
            })}
          </Typography.Text>
        )}
        {isTrialSubscription(subscription) ? (
          <TrialStatus />
        ) : (
          <Periods subscription={subscription} />
        )}
      </Flex>
      {!isCanceled && !isSoloPlan && <SeatsQuotaCard />}
      {actions.length > 0 && <Space>{actions.map((x) => x)}</Space>}
      <PricingModal
        open={isPricingModalOpen}
        onClose={() => setIsPricingModalOpen(false)}
      />
    </Flex>
  );
};
export default SubscriptionDetail;

const StyledTitle = styled(Typography.Title)`
  margin-top: 0;
`;

interface PeriodsProps {
  subscription: OrganizationSubscription;
}

const Periods: React.FC<PeriodsProps> = ({ subscription }) => {
  const t = useTranslate();
  const locale = useGetLocale();
  const currentLocale = locale();
  const dayjsLocale = currentLocale?.split("-")[0] ?? "en";

  return (
    <>
      <Typography.Text>
        {t("pages.subscription.startDate")}:{" "}
        <DateField
          format="LL"
          value={addDateTz(subscription?.start_date)}
          locales={dayjsLocale}
        />
      </Typography.Text>
      <Typography.Text>
        {t("pages.subscription.endDate")}:{" "}
        <DateField
          format="LL"
          value={addDateTz(subscription?.expiry)}
          locales={dayjsLocale}
        />
      </Typography.Text>
    </>
  );
};

const TrialStatus: React.FC = () => {
  const t = useTranslate();
  return <div>{t("pages.subscription.detail.trialStatus")}</div>;
};
